"use client"

import { YaleAssureLock2 } from "./YaleAssureLock2"
import { NestLearningThermostat } from "./NestLearningThermostat"
import { MinutHomeSensor } from "./MinutHomeSensor"
import { UnifiG6Studio } from "./UnifiG6Studio"
import type { HeroMotion } from "./hero-motion"

export type HeroDeviceKind = "yale" | "nest" | "minut" | "camera"
export type HeroAppearance = "material" | "clay"
export type HeroCameraFinish = "black" | "white"
export type HeroDeviceProps = {
  kind: HeroDeviceKind
  appearance: HeroAppearance
  motion: HeroMotion
  paused: boolean
  cameraFinish?: HeroCameraFinish
}

/** The shared hero devices; hosts supply layout, accessible labels, and a motion clock. */
export function HeroDevice({
  kind, appearance, motion, paused, cameraFinish = "white",
}: HeroDeviceProps) {
  const clay = appearance === "clay"
  if (kind === "yale") {
    return (
      <YaleAssureLock2
        appearance={clay ? "clay" : "material"}
        finish="black"
        isLocked={!motion.unlocked}
        keypadLit
        pressedKey={motion.pressedKey}
        feedback={motion.accepted ? "success" : "idle"}
        feedbackFrame={motion.feedbackFrame}
      />
    )
  }
  if (kind === "nest") {
    const cooling = motion.nestMode === "cool" && motion.nestCoolingTarget < 72
    const heating = motion.nestMode === "heat"
    return (
      <NestLearningThermostat
        appearance={clay ? "clay" : "realistic"}
        temperatureCelsius={(72 - 32) * 5 / 9}
        climateSetting={{
          hvac_mode_setting: motion.nestMode,
          fan_mode_setting: "auto",
          heating_set_point_celsius: (74 - 32) * 5 / 9,
          cooling_set_point_celsius: (motion.nestCoolingTarget - 32) * 5 / 9,
        }}
        isCooling={cooling}
        isHeating={heating}
        isFanRunning={cooling || heating}
        showFanIndicator={false}
        displayState="awake"
        displayPaused={paused}
        showLeaf={false}
      />
    )
  }
  if (kind === "minut") {
    return (
      <MinutHomeSensor
        appearance={clay ? "clay" : "realistic"}
        indicator={motion.sensorActive ? "monitoring" : "idle"}
        indicatorAppearance="soft"
        displayPaused={paused}
      />
    )
  }
  return (
    <UnifiG6Studio
      appearance={appearance}
      finish={cameraFinish}
      view="front"
      signalActive={motion.cameraActive}
      signalColor="purple"
      showActivityLed
      displayPaused={paused}
    />
  )
}
